// @ts-nocheck
/**
 * Unified mega-card: the single result surface for a unified session, shown
 * once BOTH statements have mapped. One tab row spans the whole deliverable:
 *
 *   Mapping P&L | Mapping BS | Trends | Working Capital | Insights |
 *   EBITDA Bridge | Tie-Out
 *
 * The two mapping tabs embed the existing statement cards (headerless via
 * their `embedded` prop) so category edits, saves, and per-statement exports
 * behave exactly as on the standalone pages — PATCH and export go to the
 * CHILD session ids. Trends and the EBITDA Bridge read the P&L child;
 * Working Capital and Insights read the PARENT (cross-statement endpoints);
 * Tie-Out carries a statement toggle and fetches provenance per child.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  FileSpreadsheet,
  Layers,
  XCircle,
} from 'lucide-react';

import { getProvenance } from '../../utils/agentApi';
import { prefetchAnalyses } from '../../utils/analysisPrefetch';
import { effectiveBsStatus } from './bsBalanceStatus';
import FinalMappingCard from './FinalMappingCard';
import FinalMappingCardBS from './FinalMappingCardBS';
import RatiosTab from './RatiosTab';
import InsightsTab from './InsightsTab';
import EBITDABridgeTab from './EBITDABridgeTab';
import TieOutTab from './TieOutTab';
import UnifiedExportButton from './UnifiedExportButton';
import WorkingCapitalTab from './WorkingCapitalTab';
import './WorkingCapitalTab.css';

const PL_STATUS = {
  pass: { label: 'P&L reconciled', tone: 'good', icon: CheckCircle },
  converged: { label: 'P&L reconciled', tone: 'good', icon: CheckCircle },
  exhausted: { label: 'P&L partial', tone: 'warn', icon: AlertTriangle },
  error: { label: 'P&L error', tone: 'bad', icon: XCircle },
  unknown: { label: 'P&L: no verify', tone: 'muted', icon: AlertTriangle },
};
const BS_STATUS = {
  balanced: { label: 'BS balanced', tone: 'good', icon: CheckCircle },
  // Mapping ties to the source's stated section totals; the residual gap is
  // the source workbook's own imbalance, not a mapping error.
  source_unbalanced: {
    label: 'BS matches source',
    tone: 'good',
    icon: CheckCircle,
  },
  off: { label: 'BS off', tone: 'warn', icon: AlertTriangle },
  mapping_error: { label: 'BS mapping error', tone: 'bad', icon: XCircle },
  error: { label: 'BS error', tone: 'bad', icon: XCircle },
  unknown: { label: 'BS: no check', tone: 'muted', icon: AlertTriangle },
};

const TABS = [
  { id: 'pnl', label: 'Mapping P&L' },
  { id: 'bs', label: 'Mapping BS' },
  { id: 'trends', label: 'Trends' },
  { id: 'wc', label: 'Working Capital' },
  { id: 'insights', label: 'Insights' },
  { id: 'ebitda', label: 'EBITDA Bridge' },
  { id: 'tieout', label: 'Tie-Out' },
];

// Per-statement provenance for the Tie-Out tab (TieOutTab is presentational;
// the host owns the fetch, mirroring FinalMappingCard's arrangement).
const useProvenance = (sessionId) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = useCallback(() => {
    if (!sessionId) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    let active = true;
    setState({ loading: true, data: null, error: null });
    getProvenance(sessionId)
      .then((d) => active && setState({ loading: false, data: d, error: null }))
      .catch(
        (err) =>
          active &&
          setState({
            loading: false,
            data: null,
            error: err.message || 'Could not load tie-out',
          }),
      );
    return () => {
      active = false;
    };
  }, [sessionId]);
  useEffect(() => load(), [load]);
  return { ...state, reload: load };
};

// `ExportControl` is the header's export component. It defaults to the Full
// Statements download popover; a host with a different deliverable (Databook)
// passes its own, which receives the same props.
const FinalMappingCardUnified = ({
  parentId,
  pnl,
  bs,
  onPnlMappingUpdated,
  onBsMappingUpdated,
  runComplete = false,
  ExportControl = UnifiedExportButton,
}) => {
  // A statement can be missing when its child run failed — the card renders
  // whatever survived, hiding the tabs that need the absent statement.
  const hasPnl = !!pnl?.mapping?.accounts?.length;
  const hasBs = !!bs?.mapping?.accounts?.length;

  const [activeTab, setActiveTab] = useState(hasPnl ? 'pnl' : 'bs');
  const [tieStatement, setTieStatement] = useState(hasPnl ? 'pnl' : 'bs');
  const [tieFilter, setTieFilter] = useState('all');
  // Post-download outcome note from the header export (a ticked part was
  // skipped server-side). Cleared when the popover reopens.
  const [exportNotice, setExportNotice] = useState(null);

  // Kick off every analysis the deliverable carries as soon as the run is
  // over — Trends + EBITDA Bridge on the P&L child, Insights + Working
  // Capital on the parent — so the tabs land pre-computed and the export
  // popover finds the artifacts without anyone clicking through first.
  // Gated on runComplete because the generate endpoints 409 mid-run.
  // Insights degrades server-side to a P&L-only pass when the BS is missing,
  // so it only needs the P&L; working capital joins both statements and its
  // endpoint 404s without them.
  useEffect(() => {
    if (!runComplete) return;
    prefetchAnalyses({
      pnlSessionId: hasPnl ? pnl?.sessionId : null,
      insightsSessionId: hasPnl ? parentId : null,
      wcSessionId: hasPnl && hasBs ? parentId : null,
    });
  }, [runComplete, parentId, pnl?.sessionId, hasPnl, hasBs]);

  const plMeta =
    PL_STATUS[pnl?.mapping?.reconciliation?.status] || PL_STATUS.unknown;
  const bsMeta = BS_STATUS[effectiveBsStatus(bs?.mapping)] || BS_STATUS.unknown;
  const PlIcon = plMeta.icon;
  const BsIcon = bsMeta.icon;

  // Tabs that need an absent statement are hidden: pnl/trends/ebitda/insights
  // read the P&L child (the parent insights endpoint degrades to a P&L-only
  // pass when the BS is missing), wc joins both statements and its endpoint
  // 404s without them. Tie-Out works with either statement.
  const visibleTabs = TABS.filter((t) => {
    if (
      t.id === 'pnl' ||
      t.id === 'trends' ||
      t.id === 'ebitda' ||
      t.id === 'insights'
    )
      return hasPnl;
    if (t.id === 'bs') return hasBs;
    if (t.id === 'wc') return hasPnl && hasBs;
    return hasPnl || hasBs;
  });
  const effectiveTab = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : visibleTabs[0]?.id;
  const effectiveTieStatement =
    tieStatement === 'bs' && hasBs ? 'bs' : hasPnl ? 'pnl' : 'bs';

  const plProv = useProvenance(
    effectiveTab === 'tieout' ? pnl?.sessionId : null,
  );
  const bsProv = useProvenance(
    effectiveTab === 'tieout' && effectiveTieStatement === 'bs'
      ? bs?.sessionId
      : null,
  );
  const tie = effectiveTieStatement === 'bs' ? bsProv : plProv;

  const plName = pnl?.mapping?.file?.filename;
  const bsName = bs?.mapping?.file?.filename;
  const title =
    plName && bsName && plName !== bsName
      ? `${plName} + ${bsName}`
      : plName || bsName || 'Unified statements';

  return (
    <div className="fmc-card">
      <div className="fmc-header">
        <div className="fmc-header-left">
          <Layers size={14} />
          <span className="fmc-filename" title={title}>
            {title}
          </span>
        </div>
        <div className="fmc-header-right">
          {hasPnl ? (
            <div className={`fmc-status fmc-status-${plMeta.tone}`}>
              <PlIcon size={12} />
              <span>{plMeta.label}</span>
            </div>
          ) : (
            <div className="fmc-status fmc-status-bad">
              <XCircle size={12} />
              <span>P&L failed</span>
            </div>
          )}
          {hasBs ? (
            <div className={`fmc-status fmc-status-${bsMeta.tone}`}>
              <BsIcon size={12} />
              <span>{bsMeta.label}</span>
            </div>
          ) : (
            <div className="fmc-status fmc-status-bad">
              <XCircle size={12} />
              <span>BS failed</span>
            </div>
          )}
          <ExportControl
            parentId={parentId}
            pnlSessionId={pnl?.sessionId}
            bsSessionId={bs?.sessionId}
            hasPnl={hasPnl}
            hasBs={hasBs}
            onNotice={setExportNotice}
          />
        </div>
      </div>

      {/* The Full Statements popover reports a plain string; a custom export
          control may report rendered content, which has no tooltip form. */}
      {exportNotice && (
        <div
          className="fmc-header-notice"
          title={typeof exportNotice === 'string' ? exportNotice : undefined}
        >
          {exportNotice}
        </div>
      )}

      <div className="fmc-tabs" role="tablist">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={effectiveTab === t.id}
            className={`fmc-tab ${effectiveTab === t.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {effectiveTab === 'pnl' ? (
        <FinalMappingCard
          embedded
          mapping={pnl?.mapping}
          sessionId={pnl?.sessionId}
          onMappingUpdated={onPnlMappingUpdated}
        />
      ) : effectiveTab === 'bs' ? (
        <FinalMappingCardBS
          embedded
          mapping={bs?.mapping}
          sessionId={bs?.sessionId}
          onMappingUpdated={onBsMappingUpdated}
        />
      ) : effectiveTab === 'trends' ? (
        <RatiosTab sessionId={pnl?.sessionId} />
      ) : effectiveTab === 'wc' ? (
        <WorkingCapitalTab sessionId={parentId} />
      ) : effectiveTab === 'insights' ? (
        <InsightsTab sessionId={parentId} />
      ) : effectiveTab === 'ebitda' ? (
        <EBITDABridgeTab sessionId={pnl?.sessionId} />
      ) : (
        <>
          <div className="wct-toprow" style={{ padding: '10px 14px 0' }}>
            <div className="wct-chips" role="tablist" aria-label="Statement">
              {[
                { id: 'pnl', label: 'P&L', present: hasPnl },
                { id: 'bs', label: 'Balance sheet', present: hasBs },
              ]
                .filter((s) => s.present)
                .map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={effectiveTieStatement === s.id}
                  className={`wct-chip ${
                    effectiveTieStatement === s.id ? 'wct-chip-good' : 'wct-chip-muted'
                  }`}
                  onClick={() => {
                    setTieStatement(s.id);
                    setTieFilter('all');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <FileSpreadsheet size={11} />
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
          <TieOutTab
            data={tie.data}
            loading={tie.loading}
            error={tie.error}
            filter={tieFilter}
            onFilterChange={setTieFilter}
            onRetry={tie.reload}
            hasEdits={false}
          />
        </>
      )}
    </div>
  );
};

export default FinalMappingCardUnified;
